import React, { useEffect, useState, useRef } from 'react';
import { ArrowLeft, Bot, AlertCircle, Loader2, Clock, Trash2, MessageCircle, Instagram, Send, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Conversation {
  id: number;
  estado: string;
  canal: 'Instagram' | 'WhatsApp';
  nombre_cliente_conversacion: string;
  last_msg: string;
  last_msg_time: string;
  product_category: string | null;
  account_profile_pic_url: string | null;
  bg_color: string;
  text_color: string;
}

interface Message {
  id: number;
  mensaje: string;
  mensaje_es_de_cliente: boolean;
  created_at: string;
  es_borrador: boolean;
  isNew?: boolean;
}

interface ChatInterfaceProps {
  conversation: Conversation;
  onClose: () => void;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ conversation, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [processingDraft, setProcessingDraft] = useState<'confirm' | 'decline' | null>(null);
  const [showDeclineMenu, setShowDeclineMenu] = useState(false);
  const [conversationExpired, setConversationExpired] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const declineReasons = [
    {
      id: 'not_relevant',
      label: 'No es relevante',
      description: 'La respuesta no es relevante para la consulta del cliente.'
    },
    {
      id: 'tone_incorrect',
      label: 'Tono incorrecto',
      description: 'El tono de la respuesta no es adecuado para este cliente.'
    },
    {
      id: 'information_incorrect',
      label: 'Información incorrecta',
      description: 'La respuesta contiene información incorrecta o imprecisa.'
    },
    {
      id: 'too_formal',
      label: 'Demasiado formal',
      description: 'La respuesta es demasiado formal para este cliente.'
    },
    {
      id: 'too_informal',
      label: 'Demasiado informal',
      description: 'La respuesta es demasiado informal para este cliente.'
    }
  ];

  const hasDrafts = messages.some(msg => msg.es_borrador);

  useEffect(() => {
    fetchMessages();

    // Set up auto-refresh interval
    const intervalId = setInterval(fetchMessages, 5000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [conversation.id]);

  // Handle scroll position detection
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollHeight, scrollTop, clientHeight } = container;
      const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 50;
      setShouldAutoScroll(isAtBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle auto-scrolling
  useEffect(() => {
    if (shouldAutoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, shouldAutoScroll]);

  const fetchMessages = async () => {
    setError(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      const { data, error } = await supabase
        .schema(profile.company_id)
        .from('crm_interactions')
        .select('*')
        .eq('conv_id', conversation.id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Only update messages if there are changes
      if (JSON.stringify(data) !== JSON.stringify(messages)) {
        setMessages(data || []);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError(err instanceof Error ? err.message : 'Error loading messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    setError(null);
    setShouldAutoScroll(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/64060904-237a-4beb-aa90-42fe6414c262', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationId: conversation.id,
          message: newMessage,
          user_id: user.id,
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      // Check for conversation expired error
      if (responseData.status === 'error' && responseData.response === 'Conversation expired') {
        setConversationExpired(true);
        return;
      }

      setNewMessage('');
      await fetchMessages();
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err instanceof Error ? err.message : 'Error sending message');
    } finally {
      setSending(false);
    }
  };

  const handleDeclineDraft = async (reasonId: string) => {
    setProcessingDraft('decline');
    setShowDeclineMenu(false);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      //thi surl is for declining or accepting the ai generated draft messages, it is diferrent from the one used for sending writted messages
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/5d6f7567-790a-4439-8b9b-e8cb7d8d6a56', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationId: conversation.id,
          generated_msg_confirmation: false,
          decline_reason_id: reasonId,
          user_id: user.id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to decline draft');
      }

      await fetchMessages();
    } catch (err) {
      console.error('Error declining draft:', err);
      setError(err instanceof Error ? err.message : 'Error declining draft');
    } finally {
      setProcessingDraft(null);
    }
  };

  const handleConfirmDraft = async () => {
    setProcessingDraft('confirm');
    setConversationExpired(false);
    setShouldAutoScroll(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/5d6f7567-790a-4439-8b9b-e8cb7d8d6a56', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationId: conversation.id,
          generated_msg_confirmation: true,
          decline_reason_id: "",
          user_id: user.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || `Server responded with status: ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData.status === 'error') {
        throw new Error(responseData.message || 'Failed to confirm draft');
      }

      // Check for conversation expired error
      if (responseData.status === 'error' && responseData.response === 'Conversation expired') {
        setConversationExpired(true);
        return;
      }

      await fetchMessages();
    } catch (err) {
      console.error('Error confirming draft:', err);
      const errorMessage = err instanceof Error ? err.message : 'Error confirming draft';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setProcessingDraft(null);
    }
  };

  const handleStatusUpdate = async (newStatus: 'sold' | 'inactive') => {
    setUpdatingStatus(true);
    setError(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      const { error: updateError } = await supabase
        .schema(profile.company_id)
        .from('conversaciones')
        .update({ estado: newStatus })
        .eq('id', conversation.id);

      if (updateError) throw updateError;

      // Update local conversation state
      conversation.estado = newStatus;
    } catch (err) {
      console.error('Error updating conversation status:', err);
      setError(err instanceof Error ? err.message : 'Error updating conversation status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === now.toDateString()) {
      return `Hoy ${date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    if (date.toDateString() === yesterday.toDateString()) {
      return `Ayer ${date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    return date.toLocaleString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getInitial = (name: string) => name.charAt(0).toUpperCase();

  return (
    <div className="flex flex-col h-full bg-white rounded-2xl">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="relative">
            {conversation.account_profile_pic_url ? (
              <img
                src={conversation.account_profile_pic_url}
                alt={conversation.nombre_cliente_conversacion}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-medium"
                style={{
                  backgroundColor: conversation.bg_color,
                  color: conversation.text_color,
                }}
              >
                {getInitial(conversation.nombre_cliente_conversacion)}
              </div>
            )}
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full shadow-sm flex items-center justify-center">
              {conversation.canal === 'Instagram' ? (
                <Instagram className="w-3 h-3 text-pink-600" />
              ) : (
                <MessageCircle className="w-3 h-3 text-green-500" />
              )}
            </div>
          </div>
          <div>
            <h2 className="font-medium">{conversation.nombre_cliente_conversacion}</h2>
            <p className="text-sm text-gray-500">
              Última actividad: {formatDate(conversation.last_msg_time)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleStatusUpdate('sold')}
            disabled={updatingStatus || conversation.estado === 'sold'}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
              conversation.estado === 'sold' 
                ? 'bg-emerald-600 text-white cursor-not-allowed'
                : conversation.estado === 'inactive'
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
            }`}
          >
            {updatingStatus ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              conversation.estado === 'sold' ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )
            )}
            {conversation.estado === 'sold' ? 'Venta registrada' : 'Registrar venta'}
          </button>
          <button
            onClick={() => handleStatusUpdate('inactive')}
            disabled={updatingStatus || conversation.estado === 'inactive'}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
              conversation.estado === 'inactive' 
                ? 'bg-red-600 text-white cursor-not-allowed'
                : conversation.estado === 'sold'
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            {updatingStatus ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              conversation.estado === 'inactive' ? (
                <XCircle className="w-4 h-4" />
              ) : (
                <XCircle className="w-4 h-4" />
              )
            )}
            {conversation.estado === 'inactive' ? 'Desestimado' : 'Desestimar'}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="m-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}

      {/* Conversation Expired Message */}
      {conversationExpired && (
        <div className="m-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 text-amber-700">
          <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Conversación expirada</p>
            <p className="text-sm">Debido a las políticas de Meta, solo se pueden enviar mensajes hasta 24 horas después de la última interacción del cliente.</p>
          </div>
        </div>
      )}

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : messages.length > 0 ? (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex flex-col ${
                !message.mensaje_es_de_cliente ? 'items-end' : 'items-start'
              } ${message.isNew ? 'animate-fade-in' : ''}`}
            >
              <div
                className={`max-w-[70%] p-3 rounded-lg ${
                  !message.mensaje_es_de_cliente
                    ? 'bg-blue-500 text-white'
                    : message.es_borrador
                    ? 'bg-blue-50 border border-blue-200'
                    : 'bg-gray-100'
                } ${message.isNew ? 'animate-message-appear' : ''}`}
              >
                {message.es_borrador && (
                  <div className="flex items-center gap-2 mb-2 text-blue-600">
                    <Bot className="w-4 h-4" />
                    <span className="text-sm font-medium">Borrador IA</span>
                  </div>
                )}
                <p className="whitespace-pre-wrap">{message.mensaje}</p>
              </div>
              <span className="text-xs text-gray-500 mt-1 px-1">
                {formatDate(message.created_at)}
              </span>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            No hay mensajes en esta conversación
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Draft Messages Section */}
      {hasDrafts && !conversationExpired && (
        <div className="border-t bg-blue-50 p-4 rounded-b-2xl">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-sm font-medium text-blue-800 mb-2">
              Respuesta creada con IA
            </h3>
            <div className="space-y-4">
              {messages
                .filter((msg) => msg.es_borrador)
                .map((draft) => (
                  <div key={draft.id} className="bg-white p-4 rounded-lg shadow-sm">
                    <p className="whitespace-pre-wrap mb-4">{draft.mensaje}</p>
                    <div className="flex gap-2">
                      <button
                        onClick={handleConfirmDraft}
                        disabled={processingDraft !== null}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {processingDraft === 'confirm' ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          'Enviar respuesta'
                        )}
                      </button>
                      <button
                        onClick={() => setShowDeclineMenu(true)}
                        disabled={processingDraft !== null}
                        className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {processingDraft === 'decline' ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Descartando...
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4" />
                            Descartar
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Decline Menu */}
      {showDeclineMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full animate-modal-appear">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              ¿Por qué deseas descartar esta respuesta?
            </h3>
            <div className="space-y-2">
              {declineReasons.map((reason) => (
                <button
                  key={reason.id}
                  onClick={() => handleDeclineDraft(reason.id)}
                  className="w-full p-4 text-left hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="font-medium mb-1">{reason.label}</div>
                  <p className="text-sm text-gray-500">{reason.description}</p>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowDeclineMenu(false)}
              className="mt-4 w-full px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Message Input */}
      {!hasDrafts && !conversationExpired && (
        <div className="border-t bg-white p-4 rounded-b-2xl">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            className="max-w-4xl mx-auto"
          >
            <div className="flex gap-4">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Escribe tu mensaje..."
                className="flex-1 p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={sending || !newMessage.trim()}
                className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Enviar
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};